'use client'

import type { FC } from 'react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { PUBLIC_APP_NAME } from '~/core/constants/env'
import { removeEndSplash } from '~/core/utilities/common'

import useNotificationStore from '~/lib/features/notifications/store'

const AppAppHeadMetaTags: FC<{
  title: string
  description?: string
  keywords?: string
  url?: string
  image?: string
  noUseHrefLang?: boolean
  image_alt?: string
  alternateLink?: { url: string; lang: string }
  hideFavicon?: boolean
}> = ({
  title,
  description,
  url,
  keywords,
  image,
  alternateLink,
  image_alt,
  hideFavicon = false
}) => {
  const { i18n } = useTranslation()
  const { notificationTotalUnreadCount } = useNotificationStore()

  useEffect(() => {
    // Update title
    const finalTitle =
      notificationTotalUnreadCount > 0 ? `(${notificationTotalUnreadCount}) ${title}` : title

    document.title = finalTitle

    // Helper function to updates meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta')
        meta.name = name
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    const updatePropertyMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('property', property)
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    // Update meta tags
    if (description) {
      updateMetaTag('description', description)
      updatePropertyMetaTag('og:description', description)
      updatePropertyMetaTag('twitter:description', description)
    }

    if (keywords) {
      updateMetaTag('keywords', keywords)
    }

    if (image) {
      updatePropertyMetaTag('og:image', image)
      updatePropertyMetaTag('twitter:image', image)
      updatePropertyMetaTag('og:image:secure_url', image)
      updatePropertyMetaTag('og:image:width', '1200')
      updatePropertyMetaTag('og:image:height', '630')
      updatePropertyMetaTag('og:image:type', 'image/png')
      if (image_alt) {
        updatePropertyMetaTag('og:image:alt', image_alt)
      }
    }

    updatePropertyMetaTag('og:title', title)
    updatePropertyMetaTag('twitter:title', title)
    updatePropertyMetaTag('og:site_name', title)

    if (url) {
      updatePropertyMetaTag('og:url', removeEndSplash(url))
    }

    // Update canonical link
    if (url) {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
      if (!canonical) {
        canonical = document.createElement('link')
        canonical.rel = 'canonical'
        document.head.appendChild(canonical)
      }
      canonical.href = url
    }

    // Update alternate link
    if (alternateLink) {
      let alternate = document.querySelector(
        `link[hrefLang="${alternateLink.lang}"]`
      ) as HTMLLinkElement
      if (!alternate) {
        alternate = document.createElement('link')
        alternate.rel = 'alternate'
        alternate.hreflang = alternateLink.lang
        document.head.appendChild(alternate)
      }
      alternate.href = alternateLink.url
    }

    // Update content-language
    updateMetaTag('content-language', i18n.language)

    // Update viewport nếu chưa có
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewport = document.createElement('meta')
      viewport.name = 'viewport'
      viewport.content = 'initial-scale=1.0, width=device-width'
      document.head.appendChild(viewport)
    }

    // Update robots
    updatePropertyMetaTag('robots', 'noodp,index,follow')
    updatePropertyMetaTag('og:type', 'website')

    // Facebook App ID
    if (process.env.NEXT_PUBLIC_FACEBOOK_APP_ID) {
      updatePropertyMetaTag('fb:app_id', process.env.NEXT_PUBLIC_FACEBOOK_APP_ID)
    }

    // Twitter site
    if (PUBLIC_APP_NAME) {
      updateMetaTag('twitter:site', `@${PUBLIC_APP_NAME}`)
    }

    // Favicon
    if (!hideFavicon && !document.querySelector('link[rel="icon"]')) {
      const favicon = document.createElement('link')
      favicon.rel = 'icon'
      favicon.href = '/favicon.ico'
      document.head.appendChild(favicon)
    }
  }, [
    title,
    description,
    keywords,
    url,
    image,
    image_alt,
    alternateLink,
    i18n.language,
    notificationTotalUnreadCount,
    hideFavicon
  ])

  return null
}

export default AppAppHeadMetaTags
